import React, { useState } from 'react';
import { useAuth } from '../../core/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../core/config/firebase';
import { validateUsernameContent } from '../../core/utils/usernameModeration';
import './AuthModal.css';

export default function AuthModal({ isOpen, onClose, initialMode = 'signup' }) {
  const [mode, setMode] = useState(initialMode); // 'signup', 'signin', or 'forgot-password'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { signup, signin, resendVerificationEmail } = useAuth();
  const navigate = useNavigate();

  // Reset form when switching modes
  const handleModeSwitch = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccess(false);
    setShowResendVerification(false);
  };

  // Close modal and reset
  const handleClose = () => {
    setEmail('');
    setPassword('');
    setUsername('');
    setError('');
    setSuccess(false);
    setShowResendVerification(false);
    setLoading(false);
    onClose();
  };

  // Handle click outside modal
  const handleOverlayClick = (e) => {
    if (e.target.className === 'auth-modal-overlay') {
      handleClose();
    }
  };

  // Sign Up handler
  async function handleSignUp(e) {
    e.preventDefault();

    // Frontend validation
    if (!username.trim()) {
      return setError('Username is required');
    }

    // Check username for inappropriate content
    const usernameValidation = validateUsernameContent(username);
    if (!usernameValidation.isValid) {
      return setError(usernameValidation.reason);
    }

    if (!email.endsWith('@bison.howard.edu')) {
      return setError('You must use a @bison.howard.edu email address');
    }

    if (password.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    if (!/\d/.test(password)) {
      return setError('Password must contain at least one number');
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return setError('Password must contain at least one special character');
    }

    try {
      setError('');
      setLoading(true);
      await signup(email, password, username);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to create an account');
    } finally {
      setLoading(false);
    }
  }

  // Sign In handler
  async function handleSignIn(e) {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);
      setShowResendVerification(false);

      const user = await signin(email, password);

      // Check if email is verified
      if (!user.emailVerified) {
        setError('Please verify your email before signing in. Check your inbox for the verification link.');
        setShowResendVerification(true);
        setLoading(false);
        return;
      }

      // Close modal and stay on home page
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  }

  async function handleResendVerification() {
    try {
      setError('');
      await resendVerificationEmail();
      setError('Verification email sent! Please check your inbox.');
    } catch (err) {
      setError('Failed to send verification email. Please try again.');
    }
  }

  // Forgot Password handler
  async function handleForgotPassword(e) {
    e.preventDefault();

    if (!email) {
      return setError('Please enter your email address');
    }

    try {
      setError('');
      setLoading(true);
      await sendPasswordResetEmail(auth, email);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  // Success view for sign up
  if (success && mode === 'signup') {
    return (
      <div className="auth-modal-overlay" onClick={handleOverlayClick}>
        <div className="auth-modal">
          <button className="auth-modal-close" onClick={handleClose} aria-label="Close">
            ×
          </button>
          <div className="auth-modal-content">
            <h2>Check Your Email</h2>
            <p className="success-message">
              We've sent a verification email to <strong>{email}</strong>
            </p>
            <p>Please verify your email before signing in.</p>
            <button
              onClick={() => {
                setSuccess(false);
                handleModeSwitch('signin');
              }}
              className="btn-primary"
            >
              Go to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success view for forgot password
  if (success && mode === 'forgot-password') {
    return (
      <div className="auth-modal-overlay" onClick={handleOverlayClick}>
        <div className="auth-modal">
          <button className="auth-modal-close" onClick={handleClose} aria-label="Close">
            ×
          </button>
          <div className="auth-modal-content">
            <h2>Check Your Email</h2>
            <p className="success-message">
              We've sent a password reset link to <strong>{email}</strong>
            </p>
            <p>Click the link in the email to reset your password.</p>
            <button
              onClick={() => {
                setSuccess(false);
                handleModeSwitch('signin');
              }}
              className="btn-primary"
            >
              Go to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-modal-overlay" onClick={handleOverlayClick}>
      <div className="auth-modal">
        <button className="auth-modal-close" onClick={handleClose} aria-label="Close">
          ×
        </button>

        <div className="auth-modal-content">
          <h2>HU Games</h2>
          <p className="auth-modal-subtitle">by Tribe</p>

          {/* Tab Switcher */}
          {mode !== 'forgot-password' && (
            <div className="auth-tabs">
              <button
                className={`auth-tab ${mode === 'signup' ? 'active' : ''}`}
                onClick={() => handleModeSwitch('signup')}
              >
                Sign Up
              </button>
              <button
                className={`auth-tab ${mode === 'signin' ? 'active' : ''}`}
                onClick={() => handleModeSwitch('signin')}
              >
                Sign In
              </button>
            </div>
          )}

          {/* Forgot Password Header */}
          {mode === 'forgot-password' && (
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ fontSize: '20px', color: '#000', marginBottom: '10px' }}>
                Reset Password
              </h3>
              <p style={{ fontSize: '14px', color: '#666' }}>
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>
          )}

          {error && (
            <div className={error.includes('sent') ? 'success-message' : 'error-message'}>
              {error}
            </div>
          )}

          {/* Sign Up Form */}
          {mode === 'signup' && (
            <form onSubmit={handleSignUp}>
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Choose a username"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Howard Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="yourname@bison.howard.edu"
                  required
                />
                <small>Must be a @bison.howard.edu email</small>
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <small>Must include a number and special character</small>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                {loading ? 'Creating Account...' : 'Sign Up'}
              </button>
            </form>
          )}

          {/* Sign In Form */}
          {mode === 'signin' && (
            <form onSubmit={handleSignIn}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="yourname@bison.howard.edu"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={() => handleModeSwitch('forgot-password')}
                >
                  Forgot Password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>

              {showResendVerification && (
                <button
                  onClick={handleResendVerification}
                  className="btn-secondary"
                  style={{ marginTop: '10px' }}
                  type="button"
                >
                  Resend Verification Email
                </button>
              )}
            </form>
          )}

          {/* Forgot Password Form */}
          {mode === 'forgot-password' && (
            <form onSubmit={handleForgotPassword}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="yourname@bison.howard.edu"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>

              <button
                type="button"
                onClick={() => handleModeSwitch('signin')}
                className="btn-secondary"
                style={{ marginTop: '10px' }}
              >
                Back to Sign In
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
